/**
 * Hooks for AI usage queries
 */

import { useQuery } from '@tanstack/react-query';
import {
  fetchSummary,
  fetchTimeseries,
  fetchMyUsage,
  type SummaryQuery,
  type TimeseriesQuery,
  type SummaryResponse,
  type TimeseriesResponse,
  type MeResponse,
} from '@services/aiUsageService';

/**
 * Hook for fetching usage summary with grouping and filtering
 */
export function useSummary(query: SummaryQuery): {
  data: SummaryResponse | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const queryKey = [
    'usageSummary',
    query.start,
    query.end,
    query.groupBy?.join('|') ?? '',
    query.project_id,
  ];

  const { data, isLoading, error } = useQuery<SummaryResponse>({
    queryKey,
    queryFn: () => fetchSummary(query),
  });

  return { data, isLoading, error };
}

/**
 * Hook for fetching usage timeseries data
 */
export function useTimeseries(query: TimeseriesQuery): {
  data: TimeseriesResponse | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const queryKey = [
    'usageTimeseries',
    query.start,
    query.end,
    query.project_id,
  ];

  const { data, isLoading, error } = useQuery<TimeseriesResponse>({
    queryKey,
    queryFn: () => fetchTimeseries(query),
  });

  return { data, isLoading, error };
}

/**
 * Hook for fetching the current user's own usage
 */
export function useMyUsage(query: Omit<SummaryQuery, 'project_id'>): {
  data: MeResponse | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const queryKey = [
    'myUsage',
    query.start,
    query.end,
    query.groupBy?.join('|') ?? '',
  ];

  const { data, isLoading, error } = useQuery<MeResponse>({
    queryKey,
    queryFn: () => fetchMyUsage(query),
  });

  return { data, isLoading, error };
}
